import { FC, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"

import { Alert, Button, Col, Collapse, DatePicker, Divider, Drawer, Flex, Form, InputNumber, Row, Select, Space, Tabs, Typography } from "antd"
import { TabsProps } from "antd/lib"
import dayjs from "dayjs"

import { DeliveryAddressesAddForm, DeliveryAddressesAddFormProps } from "@/components/DeliveryAddresses/DeliveryAddressesAddForm"
import { DeliveryForm, DeliveryFormRef } from "@/components/Order/DeliveryForm"
import { FuelQuoteAddFormValues, OrderAddFormValues, OrderData, TransportAddFormValues } from "@/components/Order/OrderAddForm"
import { OrderDeliveryTable } from "@/components/Order/OrderDeliveryTable/OrderDeliveryTable"
import { OrderFuelTable } from "@/components/Order/OrderFuelTable"
import { TransportTable } from "@/components/Transport/TransportTable"
import { Resource, Scope } from "@/constants/permission"
import { getCustomersSelectList } from "@/http/customer"
import { getFuelForOrder } from "@/http/fuel"
import { getNotArchivedFuelTypesByDepot } from "@/http/fuelType"
import { markAsSelectTransport } from "@/http/shippingSchedule"

import classes from "./BulkOrderSelectTransportForm.module.scss"

import { useDepotSelectListQuery } from "@/hook/Dictionaries/useDepotQuery"
import { useDriverForOrderQuery } from "@/hook/Dictionaries/useDriverQuery"
import { useTransportForOrderQuery } from "@/hook/Dictionaries/useTransportQuery"
import { useOrderMutation } from "@/hook/Orders/useOrderMutation"
import { useTimeUnloadFuelMutation } from "@/hook/TimeUnloadFuel/useTimeUnloadFuelMutation"
import { usePermission } from "@/hook/usePermission"

import { IBulkOrder, IShippingSchedule } from "@/types/IBulkOrder"
import { Customer, ICustomer, ICustomerSelectListItem } from "@/types/ICustomer"
import { IDeliveryAddress } from "@/types/IDeliveryAddress"
import { IDriver } from "@/types/IDriver"
import { convertFuelAmountToLitres, convertFuelAmountToTonn, IFuel, IFuelOrder } from "@/types/IFuel"
import { IFuelType } from "@/types/IFuelType"
import { DeliveryType, deliveryTypes, ExchangeType, exchangeTypes, IOrderCreate, IOrderFuel, ITranportPairOrder } from "@/types/IOrder"
import { ITimeUnloadFuel } from "@/types/ITimeUnloadFuel"
import { ITransport, ITransportClass } from "@/types/ITransport"

import { filterSelectOption } from "@/utils/filterSelectOption"
import { formatDate } from "@/utils/formatDate"
import { getMoney } from "@/utils/formatNumber"
import { formatTransport } from "@/utils/formatString"

const { Text } = Typography

export interface BulkOrderSelectTransportFormProps{
    open(shedule: IShippingSchedule, bulkOrder?: IBulkOrder, customer?: Customer, refetch?: () => void): void
    close(): void
}

export interface BulkOrderSelectTransportFormValues {
  date: Date
  customerId: number
  deliveryType: DeliveryType
  exchangeType?: ExchangeType
  wishPeriodTime?: string
  isReserve: boolean
  transports: TransportAddFormValues[]
  fuelQuotes: FuelQuoteAddFormValues[]
  deliveryAddressId?: number
  deliveryCost?: number
  pump?: boolean
  deliveryCommentary?: string
  depotId: number
  fuelTypeId: number
  amount: number
  price: number
  isFuture: boolean
  withPayment: boolean,
  isCustomDeliveryCost: boolean,
  customDeliveryCost: number,

  wishTimeSlotId: number,
  wishTimeSlot: string,
  driverId?: number | undefined,
  truckId?: number | undefined,
  pickupId?: number | undefined,
  fuelAmoutTransport: number,
  density: number,
  volume: number,
  weight: number
}

enum InputValueOrWeight{
  volume,
  weight
}

export const BulkOrderSelectTransportForm = forwardRef<BulkOrderSelectTransportFormProps>((props, ref) => {
    const { hasPermission } = usePermission()
    const [form] = Form.useForm<BulkOrderSelectTransportFormValues>()
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [activeKey, setActiveKey] = useState("1")
    const [shippingSchedule, setShippingSchedule] = useState<IShippingSchedule | undefined>(undefined)
    const [bulkOrder, setBulkOrder] = useState<IBulkOrder | undefined>(undefined)
    const [isCustomerOrder, setIsCustomerOrder] = useState<boolean>(false)
    const [customers, setCustomers] = useState<ICustomerSelectListItem[]>([])
    const [customer, setCustomer] = useState<ICustomer>()
    const [isDriverChosen, setIsDriverChosen] = useState(false)
    const [isTruckChosen, setIsTruckChosen] = useState(false)
    const [chosenTransports, setChosenTransports] = useState<ITranportPairOrder[]>([])
    const [isFuelSelect, setIsFuelSelect] = useState<boolean>(false)
    const [isFuelLoaded, setIsFuelLoaded] = useState<boolean>(false)
    const [fuelTypes, setFuelTypes] = useState<IFuelType[]>([])
    const [fuel, setFuel] = useState<IFuelOrder>()
    const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined)
    const [datesInRange, setDatesInRange] = useState<string[]>([])
    const [chosenFuels, setChosenFuels] = useState<IOrderFuel[]>([])
    const [hasWishTime, setHasWishTime] = useState<boolean>(false)
    const [isTransportSelect, setIsTransportSelect] = useState<boolean>(false)
    const [timeUnloadFuels, setTimeUnloadFuels] = useState<ITimeUnloadFuel[]>([])
    const [isTimeUnloadChosen, setIsTimeUnloadChosen] = useState(false)
    const [chosenTimeUnloadFuel, setChosenTimeUnloadFuel] = useState<ITimeUnloadFuel | undefined>(undefined)
    const [orderData, setOrderData] = useState<OrderData>()
    const [chosenDeliveryType, setChosenDeliveryType] = useState<DeliveryType>(DeliveryType.Pickup)
    const [isDelivery, setIsDelivery] = useState<boolean>(false)
    const [isReserve, setIsReserve] = useState<boolean>(false)
    const [isProcessing, setIsProcessing] = useState(false)
    const [data, setData] = useState<BulkOrderSelectTransportFormValues>()
    const [amountFuel, setAmountFuel] = useState<number>(0)
    const [price, setPrice] = useState<number>(0)
    const [ deliveryAddres, setDeliveryAddres ] = useState<IDeliveryAddress | undefined>(undefined)


    const delta: number = 3

    const onRefetch = useRef<(() => void) | undefined>()
    const deliveryForm = useRef<DeliveryFormRef>(null)
    const addressComponent = useRef<DeliveryAddressesAddFormProps>(null)
    const [isFuelAmountApprove, setIsFuelAmountApprove] = useState<boolean>(false)    

    const isAdminOrManager = useMemo(() => {
        return hasPermission(Resource.OrderReport, Scope.Read)
      }, [hasPermission])

    const { data: transports, isLoading: transportsIsLoading } =
        useTransportForOrderQuery(0, isAdminOrManager ? undefined : customer?.id)

    const { data: drivers, isLoading: driversIsLoading } =
        useDriverForOrderQuery(0, isAdminOrManager ? undefined : customer?.id)

    const { data: depots, isLoading: depotsIsLoading } = useDepotSelectListQuery()
    const { getAvailableTimeUnloadFuelsMutation } = useTimeUnloadFuelMutation()

    const { createMutation } = useOrderMutation()

    const allDrivers: IDriver[] = useMemo(() => {
          return [...(drivers?.response ?? [])]
        }, [drivers])
      
    const allTrucks: ITransport[] = useMemo(() => {
        return [
          ...(transports?.response.filter(
            (t) => (t.class === ITransportClass.Truck) || (t.class === ITransportClass.TankerTruck)
          ) ?? [])
        ]
    }, [transports])

    const allPickups: ITransport[] = useMemo(() => {
        return [
          ...(transports?.response.filter(
            (t) => t.class === ITransportClass.Pickup
          ) ?? [])
        ]
      }, [transports])

    const onDeleteTransport = (transport: ITranportPairOrder) => {
      let orderTransports: TransportAddFormValues[] =
        form.getFieldValue("transports") ?? []
      orderTransports = orderTransports.filter((ot) => ot.driverId != transport.id)
  
      refreshChosenTransports(orderTransports)
      form.setFieldValue("transports", orderTransports)
      form.setFieldValue("wishTimeSlot", undefined)
      form.setFieldValue("wishTimeSlotId",undefined)
      setIsTransportSelect(false)
      setIsTimeUnloadChosen(false)
    }

    const additionalTransportColumns = [
        {
          title: "",
          dataIndex: "delete",
          render: (_: any, transport: ITranportPairOrder) =>
            activeKey == "1" ? (
              <Button
                danger
                type="primary"
                onClick={() => onDeleteTransport(transport)}
              >
                X
              </Button>
            ) : null
        }
      ]

    useEffect(() => {
      form.setFieldValue("depotId", bulkOrder?.depotId)

      if (depots) {
        const depot = depots.response.find(
          (d) => d.id == form.getFieldValue("depotId")
        )

        if (depot) {
          setHasWishTime(depot.hasWishTime)
        }
      }
    }, [shippingSchedule, bulkOrder])

    useEffect(() => {
      if(shippingSchedule){
        form.setFieldValue("fuelTypeId", shippingSchedule?.fuelTypeId)
      }
    }, [fuelTypes])

    useEffect(() => {
      if(fuel){
        calculatePrice(form.getFieldValue("weight"))
      }
    }, [fuel])

    useImperativeHandle(ref, () => ({
        open(shedule: IShippingSchedule, bulkOrder?: IBulkOrder, customer?: Customer, refetch?: () => void) {
          onOpenHandler(shedule, bulkOrder, customer, refetch)
        },
        close() {
          onCloseHandler()
        }
    }))

    const onOpenHandler = async (shedule: IShippingSchedule, bulkOrder?: IBulkOrder, customer?: Customer, refetch?: () => void) => {
      onRefetch.current = refetch
      if(shedule){
          setShippingSchedule(shedule)
      }

      if(bulkOrder){
        setBulkOrder(bulkOrder)
        setChosenDeliveryType(bulkOrder.deliveryType)
        setIsDelivery(bulkOrder.deliveryType === DeliveryType.Delivery)
        form.setFieldValue("deliveryType", bulkOrder.deliveryType)

        getNotArchivedFuelTypesByDepot(bulkOrder.depotId).then(res => {
            setFuelTypes(res.response)
            setFuel(undefined)
            setIsFuelLoaded(false)
        })        
      }

      getCustomersSelectList().then((response) => {
        setCustomers(response?.response ?? [])
        setIsCustomerOrder(false)
      })

      if (customer) {
          setCustomers([])
          form.setFieldValue("customerId", customer.id)
          setCustomer(customer)
          setIsCustomerOrder(true)
      }
      else if (bulkOrder?.customerId) {
        form.setFieldValue("customerId", bulkOrder?.customerId)
      }
      else {
          getCustomersSelectList().then((response) => {
              setCustomers(response?.response ?? [])
              setIsCustomerOrder(false)
          })
      }

      if(shedule.startDate || shedule.endDate){
        const result: string[] = []
        let current = dayjs(shedule.startDate)
        while (current.isBefore(dayjs(shedule.endDate)) || current.isSame(dayjs(shedule.endDate))) {
          result.push(current.format("YYYY-MM-DD"))
          current = current.add(1, "day")
        }
        setDatesInRange(result)
      }

      setIsOpen(true)
    }

    const onCloseHandler = () => {
      setSelectedDate(undefined)
      setDatesInRange([])
      setIsDriverChosen(false)
      setIsTransportSelect(false)
      setIsTruckChosen(false)
      setChosenTransports([])
      setChosenFuels([])
      setActiveKey("1")
      setShippingSchedule(undefined)
      setBulkOrder(undefined)
      setPrice(0)
      setIsFuelAmountApprove(false)
      setAmountFuel(0)
      form.resetFields()
      setIsOpen(false)

      if(onRefetch.current){
        onRefetch.current()
      }
    }

    const onCustomerChange = (customerId: number) => {
        setCustomer(new Customer(customers.find(c => c.value == customerId)))
    
        refreshChosenTransports([])
        form.setFieldValue("transports", [])
        form.setFieldValue("driverId", undefined)
        form.setFieldValue("truckId", undefined)
        form.setFieldValue("pickupId", undefined)
        setIsDriverChosen(false)
        setIsTruckChosen(false)
    }

    const refreshChosenTransports = (ts: TransportAddFormValues[]) => {
      if (!transports?.response  || !transports?.response) {
        return
      }
  
      setChosenTransports(
        ts.map((t) => ({
          id: t.driverId,
          driverId: t.driverId,
          driver: allDrivers.find((d) => d.id == t.driverId),
          truckId: t.truckId,
          truck: allTrucks.find((tr) => tr.id == t.truckId),
          pickupId: t.pickupId,
          pickup: allPickups.find((p) => p.id == t.pickupId),
          wishTimeSlot: t.wishTimeSlot,
          fuelAmount: t.fuelAmount
        }))
      )
    }

    const handleDateSelect = async (date: string) => {
      setSelectedDate(date)
      const dateT = dayjs(date)
      form.setFieldValue("date", dateT)

      const depotId = form.getFieldValue("depotId")
      const dateFrom = form.getFieldValue("date")
      const fuelTypeId = form.getFieldValue("fuelTypeId")

      if (depotId && dateFrom && fuelTypeId) {
        await getFuelForOrder({
          Date: new Date(dayjs(dateFrom).format("YYYY-MM-DD")),
          FuelTypeId : fuelTypeId,
          DepotId : depotId,
          CustomerId: form.getFieldValue("customerId")
        }).then(res => {
          setFuel(res.response)
          setIsFuelLoaded(true)
        })

        if(hasWishTime && !isDelivery){
          timeUnloadFuelLoad()
        }
      }
    }

    const onAddFuelInOrder = () => {
      if(amountFuel && bulkOrder && fuel){
        const fuelType = fuelTypes?.find(ft => ft.id == form.getFieldValue("fuelTypeId")) 

        let fuelPrice = shippingSchedule?.price && shippingSchedule.price > 0 
        ? shippingSchedule.price 
        : fuel.price > 0 
          ? fuel.price 
          : fuel.lastSetPrice 

        const fuelInOrder: FuelQuoteAddFormValues  = {
          id: fuel.id,
          quoteId: fuel.quoteId,
          amount: amountFuel,
          fuelTypeId: fuelType!.id,
          fuelType: fuelType!.name,
          density: fuel.density,
          price: fuelPrice,
          addon: fuel.addon,
          isFuture: fuel.isFuture,
          limitSettingId: fuel.limitSettingId ?? 0
        }

        setChosenFuels([fuelInOrder])
        form.setFieldValue("fuelQuotes", [fuelInOrder])
        setAmountFuel(0)
        form.setFieldValue("amount", undefined)
        form.setFieldValue("volume", undefined)
        form.setFieldValue("weight", undefined)
        setPrice(0)
      }
    }

    const timeUnloadChange = () => {
      const timeUnloadId = form.getFieldValue("wishTimeSlotId")
      if (timeUnloadId){
        setIsTimeUnloadChosen(true)
        setChosenTimeUnloadFuel(timeUnloadFuels.find(t => t.id == timeUnloadId))
      }
    }

    const timeUnloadFuelLoad = async () => {
      const dateForm = form.getFieldValue("date")
      if (dateForm) {
        setTimeUnloadFuels([])
        try {
          const res = await getAvailableTimeUnloadFuelsMutation.mutateAsync({
            date: new Date(dayjs(dateForm).format("YYYY-MM-DD"))
          })
          if (res.succeeded) {
            setTimeUnloadFuels(res.response)
          }
        } catch (e) {
          console.warn(e)
          setTimeUnloadFuels([])
        }
      }
    }

    const onAddTransportInOrder = async () => {
      const driverId = form.getFieldValue("driverId")
      const truckId = form.getFieldValue("truckId")
      const pickupId = form.getFieldValue("pickupId")
      const timeUnloadId = form.getFieldValue("wishTimeSlotId")
      const fuelAmoutTransport = form.getFieldValue("fuelAmoutTransport")
  
      const orderTransports: TransportAddFormValues[] = form.getFieldValue("transports") ?? []
  
      if (
        orderTransports.some(
          (ot) =>
            ot.driverId == driverId ||
            ot.truckId == truckId ||
            (pickupId != undefined && ot.pickupId == pickupId)
        )
      ) {
        return
      }
  
      orderTransports.push({
        driverId: driverId,
        truckId: truckId,
        pickupId: pickupId,
        wishTimeSlot: timeUnloadFuels.find(tuf => tuf.id == timeUnloadId)?.slot,
        fuelAmount: fuelAmoutTransport
      })
  
      setIsTransportSelect(true)
      refreshChosenTransports(orderTransports)
      form.setFieldValue("transports", orderTransports)
      form.setFieldValue("wishTimeSlot", timeUnloadFuels.find(f => f.id == timeUnloadId)?.slot)
      form.setFieldValue("wishTimeSlotId",undefined)
      form.setFieldValue("driverId", undefined)
      form.setFieldValue("truckId", undefined)
      form.setFieldValue("pickupId", undefined)
      form.setFieldValue("fuelAmoutTransport", undefined)
      
      setIsDriverChosen(false)
      setIsTruckChosen(false)
    }

    const onFormContinueOrder = async () => {
      try {
        await form.validateFields()

        const fuels: FuelQuoteAddFormValues[] = form.getFieldValue("fuelQuotes") ?? []
        const orderTransports: TransportAddFormValues[] = form.getFieldValue("transports") ?? []
    
        const fullPrice = fuels.reduce((sum, a) => sum + (Number(a.price) * a.amount), 0)
  
        setOrderData({
          date: formatDate(form.getFieldValue("date").toString()),
          customer: {
            id: customer ? customer.id : form.getFieldValue("customerId"),
            name: customer
              ? customer.name
              : customers?.find((c) => c.value == form.getFieldValue("customerId"))
                ?.label ?? "-"
          },
          isReserve: isReserve,
          wishPeriodTime: isTimeUnloadChosen 
          ? timeUnloadFuels.find(t => t.id == form.getFieldValue("wishTimeSlotId"))?.slot : "-",
          deliveryType:
                    deliveryTypes.find(
                      (d) => d.type == form.getFieldValue("deliveryType")
                    )?.name ?? "Не выбран",
          delivery: isDelivery ? 
          {
            deliveryAddress: deliveryAddres,
            commentary: form.getFieldValue("deliveryCommentary")
          } : undefined ,
          exchangeType:
            chosenDeliveryType != DeliveryType.Delivery
              ? exchangeTypes.find(
                (e) => e.type == form.getFieldValue("exchangeType")
              )?.name ?? "Не выбран"
              : undefined,
          depot:
            depots?.response.find((d) => d.id == form.getFieldValue("depotId"))
              ?.name ?? "Не выбрана",
          depotId: form.getFieldValue("depotId"),
          transports:
            chosenDeliveryType === DeliveryType.Pickup
              ? orderTransports.map((t) => ({
                id: t.driverId,
                driverId: t.driverId,
                driver: allDrivers.find((d) => d.id == t.driverId),
                truckId: t.truckId,
                truck: allTrucks.find((tr) => tr.id == t.truckId),
                pickupId: t.pickupId,
                pickup: allPickups.find((p) => p.id == t.pickupId),
                wishTimeSlot: t.wishTimeSlot,
                fuelAmount: t.fuelAmount 
              }))
              : [],
          fuels: fuels.map((fq) => ({
            id: fq.id,
            quoteId: fq.id,
            amount: fq.amount,
            fuelTypeId: fq.fuelTypeId,
            fuelType: fq.fuelType,
            density: fq.density,
            price: Number(fq.price),
            addon: fuel?.addon ?? 0,
            isFuture: fq.isFuture,
            limitSettingId: fq.limitSettingId
          })),
          isFuture: fuels.some((fq) => fq.isFuture == true),
          deliveryPrice: 0,
          fullPrice: fullPrice,
          finalPriceText: `Общая сумма: ${getMoney(fullPrice)}`
        })

        setActiveKey("2")
      } catch (error) {
        console.log("Validation Failed:", error)
      }
    }

    const onReturnToChangeOrderBtn = () => {
      setActiveKey("1")
    }

    const onCreateOrder = () => {
      form.submit() 
    }

    const onFinish = async (data: BulkOrderSelectTransportFormValues) => {
      const fuelQuotes: FuelQuoteAddFormValues[] = form.getFieldValue("fuelQuotes") ?? []
      const orderTransports: TransportAddFormValues[] = form.getFieldValue("transports") ?? []
      const isFuture: boolean = fuelQuotes.some((fq) => fq.isFuture == true)
  
      const wishTimeSlot = form.getFieldValue("wishTimeSlot")
        if (wishTimeSlot) {
         data.wishPeriodTime = wishTimeSlot
        }
        else {
          data.wishPeriodTime = timeUnloadFuels.find(t => t.id == form.getFieldValue("wishTimeSlotId"))?.slot
        }
      data.deliveryType = isDelivery ? DeliveryType.Delivery : DeliveryType.Pickup 
      data.fuelQuotes = fuelQuotes
      data.transports = orderTransports
      data.isFuture = isFuture
      data.isReserve = isReserve
      setData(data)
  
      await tryOrder(data)
    }

    const tryOrder = async (data: OrderAddFormValues | undefined) => {
        try {
          if (data == undefined) return
    
          const request: IOrderCreate = {
            date: dayjs(data.date).format("YYYY-MM-DD"),
            deliveryType: data.deliveryType,
            delivery: {
              deliveryAddressId: data.deliveryAddressId ?? 0,
              pump: data.pump,
              commentary: data.deliveryCommentary
            },
            depotId: data.depotId,
            exchangeType: data.exchangeType,
            customerId: data.customerId,
            deliveryPrice: data?.deliveryCost ?? 0,
            wishTimeSlot: data.wishPeriodTime,
            isReserve: data.isReserve,
            transports: data.transports.map((t: TransportAddFormValues) => ({
              driverId: t.driverId,
              truckId: t.truckId,
              pickupId: t.pickupId,
              wishTimeSlot: t.wishTimeSlot,
              fuelAmount: t.fuelAmount
            })),
            fuelQuoteOrders: data.isFuture
              ? []
              : data.fuelQuotes.map((fq: FuelQuoteAddFormValues) => ({
                id: fq.quoteId,
                amount: Number(fq.amount),
                limitSettingId: fq.limitSettingId
              })),
            fuelFutureOrders: data.isFuture
              ? data.fuelQuotes.map((fq: FuelQuoteAddFormValues) => ({
                id: fq.id,
                amount: Number(fq.amount),
                avgPrice: fq.price,
                addon: fq.addon,
                limitSettingId: fq.limitSettingId == 0 ? null : fq.limitSettingId
              }))
              : [],
              newDrivers: [],
              newAddresses: [],
              newTransports: [],
              bulkOrderId: bulkOrder?.id,
              shippingScheduleId: shippingSchedule?.id
          }
    
          setIsProcessing(true)

          var response =  await createMutation.mutateAsync(request)
          if(response.succeeded && shippingSchedule?.id){
            await markAsSelectTransport(shippingSchedule.id, chosenFuels.reduce((sum, f) => sum + f.amount, 0))
          }
    
          onCloseHandler()
        } catch {
        } finally {
          setIsProcessing(false)
        }
    }

    const onWeightOrVolumeChange = (valueTypeChane: InputValueOrWeight) => {
      var amountLitres:number  = 0
      var amountTonn:number = 0
  
      switch (valueTypeChane) {
        case InputValueOrWeight.volume:
          amountLitres = form.getFieldValue("volume")
          if (amountLitres && !Number.isInteger(amountLitres)) {
            amountLitres = Math.floor(Number(amountLitres))
            form.setFieldValue("volume",amountLitres)
          }
  
          amountTonn = convertFuelAmountToTonn(amountLitres, fuel?.density ?? 1)
            form.setFieldValue("weight", amountTonn.toFixed(3))
            setAmountFuel(amountTonn) 
          break
  
        case InputValueOrWeight.weight:
          amountTonn = form.getFieldValue("weight")
          amountLitres = convertFuelAmountToLitres(amountTonn, fuel?.density ?? 1)
          if (amountLitres && !Number.isInteger(amountLitres)) {
            amountLitres = Math.floor(Number(amountLitres))
          }
          form.setFieldValue("volume",amountLitres)
          setAmountFuel(amountTonn)
          break  
      }

      if(shippingSchedule?.remainderFuel){
        setIsFuelAmountApprove((shippingSchedule?.remainderFuel + delta) >= amountTonn)
      }

      calculatePrice(form.getFieldValue("weight"))
    }

    const calculatePrice = (amount: number, newFuel?: IFuelOrder) => {
      if (amount && amount > 0) {

        if(shippingSchedule?.price && shippingSchedule.price > 0){
          const newPrice = amount * shippingSchedule.price
          setPrice(newPrice)
        }
        else if (fuel?.price && fuel.price > 0) {
          const newPrice = amount * fuel.price
          setPrice(newPrice)
        }
        else if (fuel?.lastSetPrice){
          const newPrice = amount * fuel.lastSetPrice
          setPrice(newPrice)
        }
      }
      else {
        setPrice(0)
        setAmountFuel(amount ?? 0)
      }
    }


    const onDeleteFuel = () => {
      setChosenFuels([])
      form.setFieldValue("fuelQuotes", [])
    }

    const additionalFuelColumns = [
      {
        title: "",
        dataIndex: "delete",
        render: (_: any, fuel: IOrderFuel) =>
          activeKey == "1" ? (
            <Button danger type="primary" onClick={() => onDeleteFuel()}>
              X
            </Button>
          ) : null
      }
    ]

    const tabsItems: TabsProps["items"] = [
      {
        key: "1",
        label: "Оформление заказа",
        children: 
        <>
        <DeliveryAddressesAddForm
            open={() => addressComponent.current?.open()}
            close={() => addressComponent.current?.close()} 
            onCloseAction={() => deliveryForm.current?.refetch() }
            ref={addressComponent} 
          />
        <Row hidden={isCustomerOrder} gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="customerId"
              label="Контрагент"
              rules={[
                {
                  required: !isCustomerOrder,
                  message: "Пожалуйста выберите контрагент"
                }
              ]}
            >
              <Select
                disabled={true}
                placeholder="Контрагент"
                onChange={onCustomerChange}
                showSearch
                filterOption={(inputValue, option) =>
                    filterSelectOption(inputValue, option?.label || "")
                }
                options={customers}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col>
            <Form.Item label="Выберите дату">
              <Flex wrap justify="space-between">
                {datesInRange.map((date) => (
                  <Button
                  style={{ marginRight: 10, marginBottom: 10 }}
                  key={date}
                  type={selectedDate === date ? "primary" : "default"}
                  onClick={() => handleDateSelect(date)}>
                    {dayjs(date).format("DD.MM.YYYY")}
                  </Button>
                  ))}
              </Flex>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
            <Col xs={24} md={12}>
                <Form.Item 
                  name={"depotId"}
                  label= "Нефтебаза"
                  rules={[
                    {
                      required : true,
                      message : "Пожалуйста выберите нефтебазу"
                    }
                  ]}>
                    <Select 
                      disabled={true}
                      placeholder = "Нефтебаза"
                      showSearch
                      filterOption={(inputValue, option) =>
                        filterSelectOption(
                            inputValue,
                            option?.label || ""
                        )
                      }
                      options={depots?.response.map((depot) => ({
                        value: depot.id,
                        label: depot.name
                      }))}
                    />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="date"
                  label="Дата"
                  rules={[
                    { required: true, message: "Пожалуйста выберите дату" }
                  ]}
                >
                  <DatePicker
                    disabled= {true}
                    style={{ width: "100%" }}
                    placeholder="Дата заказа"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="fuelTypeId"
                  label="Тип топлива"
                >
                    <Select
                      disabled= {true}
                      placeholder="Тип топлива"
                      showSearch
                      filterOption={(inputValue, option) =>
                        filterSelectOption(
                          inputValue,
                          option?.label || ""
                        )
                      }
                      options={fuelTypes?.map((fuelType) => ({
                        value: fuelType.id,
                        label: `${fuelType.name}`
                      }))}
                    />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="exchangeType"
                  label="Вид обменной доверенности"
                  rules={[
                    {
                      required: true,
                      message:
                          "Пожалуйста выберите Вид обменной доверенности"
                    }
                  ]}
                >
                  <Select
                    placeholder="Вид обменной доверенности"
                    showSearch
                    filterOption={(inputValue, option) =>
                        filterSelectOption(inputValue, option?.label || "")
                    }
                    options={exchangeTypes.map((type) => ({
                      value: type.type,
                      label: type.name
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            {selectedDate && chosenFuels.length === 0 && fuel && (
              <div>
                <Row gutter={16} hidden={!isFuelLoaded}>
                  {!isFuelAmountApprove && amountFuel !== 0 && (
                    <Col span={24}>
                      <Alert 
                      message={`Указанный объем топлива недоступен. Доступно для заказа: ${shippingSchedule?.remainderFuel ? shippingSchedule!.remainderFuel + delta : 0} т.`} 
                      type="warning"
                      showIcon />
                    </Col>
                  )}
                  <Col xs={12} md={6}>
                    <Form.Item label="Объем, л." name="volume">
                      <InputNumber
                        className={classes["inputNumber"]}
                        onChange={() => onWeightOrVolumeChange(InputValueOrWeight.volume)}
                        type="number"
                        placeholder="Объем"
                        min={1}
                        step={1}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Масса, т." name="weight">
                      <InputNumber
                        className={classes["inputNumber"]}
                        onChange={(e) => onWeightOrVolumeChange(InputValueOrWeight.weight)}
                        type="number"
                        placeholder="Масса"
                        min={1}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6} style={{ display: "flex", alignItems: "flex-end" }}>
                    <Button
                      disabled={price === 0 || !isFuelAmountApprove}
                      className={classes["align-center"]}
                      type="primary"
                      size="large"
                      shape="round"
                      onClick={onAddFuelInOrder}
                    >
                      Добавить{price > 0 ? ` (${getMoney(price)})` : ""}
                    </Button>
                  </Col>
                </Row>
              </div>
            )}
            {chosenFuels && chosenFuels.length !== 0 && (
            <div>
                <OrderFuelTable fuels={chosenFuels} additionalColumns={additionalFuelColumns} />
            </div>
            )}
            {isDelivery && (
              <Collapse 
                className={classes["collapse-order"]}
                ghost
                defaultActiveKey={["1"]}
                items={[
                  { key: 1,
                    label:"Доставка",
                    children: (
                      <>
                        {
                          <DeliveryForm
                            ref={deliveryForm}
                            addDeliveryAddresComponent={addressComponent}
                            form={form}
                            isActive ={isDelivery} 
                            customerId={customer?.id ?? 0}
                            fuelsTotalAmount={amountFuel}
                            setAddress={(addres: IDeliveryAddress) => setDeliveryAddres(addres)}
                          />
                        }
                      </>
                    )
                  }
                ]}
              />
            )}
            {selectedDate && !isDelivery && (
              <div style={{ marginTop: 20 }}>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item 
                    name="driverId"
                    label="Водитель"
                    rules={[
                      {
                        required: hasWishTime && chosenTransports && chosenTransports.length == 0,
                        message: "Пожалуйста выберите водителя"
                      }
                    ]}>
                      <Select
                        disabled = {isTransportSelect}
                        onChange={(e: number) =>
                            setIsDriverChosen(e != undefined)
                        }
                        placeholder="Водитель"
                        showSearch
                        filterOption={(inputValue, option) =>
                            filterSelectOption(
                                inputValue,
                                option?.label || ""
                            )
                        }
                        options={allDrivers
                            .filter(
                                (d) =>
                                    !chosenTransports.some(
                                        (ct) => ct.driverId == d.id
                                    )
                            )
                            .map((driver) => ({
                              value: driver.id,
                              label: driver.fullName
                            }))}
                        dropdownRender={(menu) => (
                            <>
                              {menu}
                              <Divider style={{ margin: "8px 0" }} />
                              <Space style={{ padding: "0 8px 4px" }}>
                                Нет водителя в списке? Добавьте в справочнике водителей!
                              </Space>
                            </>
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="truckId"
                      label="Транспорт"
                      rules={[
                        {
                          required: hasWishTime && chosenTransports && chosenTransports.length == 0,
                          message: "Пожалуйста выберите транспорт"
                        }
                      ]}
                    >
                      <Select
                        disabled = {isTransportSelect}
                        onChange={(e: number) => setIsTruckChosen(e != undefined) }
                        placeholder="Транспорт"
                        showSearch
                        filterOption={(inputValue, option) =>
                            filterSelectOption(
                                inputValue,
                                option?.label || ""
                            )
                        }
                        options={allTrucks
                            .filter(
                                (tr) =>
                                    !chosenTransports.some(
                                        (ct) => ct.truckId == tr.id
                                    )
                            )
                            .map((transport) => ({
                              value: transport.id,
                              label: formatTransport(transport)
                            }))}
                        dropdownRender={(menu) => (
                            <>
                              {menu}
                              <Divider style={{ margin: "8px 0" }} />
                              <Space style={{ padding: "0 8px 4px" }}>
                                Нет транспорта в списке? Добавьте в справочнике транспорта!
                              </Space>
                            </>
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name="pickupId" label="Прицеп">
                      <Select
                        disabled = {isTransportSelect}
                        placeholder="Прицеп"
                        showSearch
                        filterOption={(inputValue, option) =>
                            filterSelectOption(
                                inputValue,
                                option?.label || ""
                            )
                        }
                        options={allPickups
                            .filter(
                                (p) =>
                                    !chosenTransports.some(
                                        (ct) => ct.pickupId == p.id
                                    )
                            )
                            .map((transport) => ({
                              value: transport.id,
                              label: formatTransport(transport)
                            }))}
                        dropdownRender={(menu) => (
                            <>
                              {menu}
                              <Divider style={{ margin: "8px 0" }} />
                              <Space style={{ padding: "0 8px 4px" }}>
                                Нет прицепа в списке? Добавьте в справочнике транспорта!
                              </Space>
                            </>
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      hidden={!hasWishTime}
                      name="wishTimeSlotId"
                      label="Желаемый период загрузки"
                      rules={[
                        {
                          required : hasWishTime && chosenTransports && chosenTransports.length == 0 ,
                          message : "Пожалуйста выберите время отгрузки"
                        }
                      ]}
                    >
                      <Select
                        disabled= {isTransportSelect}
                        placeholder="Желаемый период загрузки"
                        showSearch
                        onChange={timeUnloadChange}
                        filterOption={(inputValue, option) =>
                          filterSelectOption(
                            inputValue,
                            option?.label || "")}
                        options={timeUnloadFuels.map((timeUnload) => ({
                          value: timeUnload.id,
                          label: `${timeUnload.slot}`
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Button
                      disabled={ (chosenTransports.length == 1 && chosenFuels.length > 0) || !isDriverChosen || !isTruckChosen
                        || (hasWishTime && !isTimeUnloadChosen)}
                      type="primary"
                      size="large"
                      shape="round"
                      onClick={onAddTransportInOrder}
                    >
                      Добавить
                    </Button>
                  </Col>
                </Row>
                <div hidden={chosenTransports.length == 0}>
                  <TransportTable
                  transports={chosenTransports}
                  additionalColumns={additionalTransportColumns}
                  orderDate={new Date(selectedDate)}/>
                </div>
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              <Button
                  disabled={ (!isDelivery && hasWishTime && chosenTransports.length == 0) || chosenFuels.length == 0 }
                  type="primary"
                  size="large"
                  shape="round"
                  onClick={onFormContinueOrder}
                >
                  {"Сформировать заказ"}
                </Button>
            </div>
        </>
      },
      {
        key: "2",
        label: "Подтверждение заказа",
        children:
        <>
          <Row gutter={16} >
            <Col xs={24} md={12}>
              <Row gutter={[12, 6]}>
                <Col span={24}>
                  <Text type="secondary">Заказчик:</Text>
                </Col>
                <Col span={24}>
                  <Text>
                    {orderData?.customer.name}
                  </Text>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row  gutter={16} >
            <Col xs={24} md={12}>
              <Row gutter={[12, 6]}>
                <Col span={24}>
                  <Text type="secondary">Дата:</Text>
                </Col>
                <Col span={24}>
                  <Text >{orderData?.date}</Text>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row  gutter={16} >
            <Col hidden={orderData?.delivery != undefined} xs={24} md={12}>
              <Row gutter={[12, 6]}>
                <Col span={24}>
                  <Text type="secondary">Вид обменной доверенности:</Text>
                </Col>
                <Col span={24}>
                  <Text >
                    {orderData?.exchangeType}
                  </Text>
                </Col>
              </Row>
            </Col>
          </Row>
          <div>
            <Collapse
            defaultActiveKey={["1"]}
            ghost
            items={[
              {
                key: "1",
                label: "Выбор топлива",
                children: (
                    <>
                      <Row  gutter={16}>
                        <Col span={24}>
                          <Row gutter={[12, 6]}>
                            <Col span={24}>
                              <Text type="secondary">Нефтебаза:</Text>
                            </Col>
                            <Col span={24}>
                              <Text >
                                {orderData?.depot}
                              </Text>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <div
                          hidden={
                              orderData?.fuels && orderData?.fuels.length == 0
                          }
                      >
                        <OrderFuelTable fuels={orderData?.fuels ?? []} />
                      </div>
                    </>
                  )
                }
              ]}
            ></Collapse>
            {isDelivery && (
              <Collapse
                className={classes["collapse-order"]}
                defaultActiveKey={["1"]}
                ghost
                items={[
                  {
                    key: "1",
                    label: "Доставка",
                    children: (
                      <>
                        <Row className={classes["order-row"]} gutter={16}>
                          <Col span={24}>
                            <OrderDeliveryTable
                              deliveryDefaultPrice={(orderData?.delivery?.deliveryAddress?.defaultDeliveryCost ?? 0) * amountFuel}
                              delivery={orderData?.delivery}
                              showPrice={true}
                              deliveryPrice={(orderData?.delivery?.deliveryAddress?.deliveryCost ?? 0) * amountFuel}
                            />
                          </Col>
                        </Row>
                      </>
                    )
                  }
                ]}
              />
            )}
            {!isDelivery && (
              <Collapse
                defaultActiveKey={["1"]}
                ghost
                items={[
                  {
                    key: "1",
                    label: "Выбор транспорта",
                    children: (
                      <>
                        <div hidden={ orderData?.transports && orderData?.transports.length == 0 }>
                          <TransportTable 
                           orderDate={selectedDate ? new Date(selectedDate) : undefined}
                           transports={orderData?.transports ?? []}/>
                        </div>
                        <Text hidden={!orderData?.transports || orderData?.transports.length > 0 }>
                          Не выбрано
                        </Text>
                      </>
                    )
                  }
                ]}
              />
            )}
          </div>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Text>
                {orderData?.finalPriceText}
              </Text>
            </Col>
          </Row>
          <Flex gap="middle">
            <Button
              type="primary"
              size="large"
              shape="round"
              loading={isProcessing}
              onClick={onCreateOrder}
            >
              {"Создать заказ"}
            </Button>
            <Button
              type="primary"
              ghost
              size="large"
              shape="round"
              onClick={onReturnToChangeOrderBtn}
            >
              Изменить
            </Button>
          </Flex>
        </>
      }
    ]

    return (
        <Drawer
        width={820}
        title = {"Заказать"}
        open={isOpen}
        onClose={onCloseHandler}
        styles={{ body: { padding: "10px 40px 80px 40px", borderRadius: "20px" } }}>
            <Form layout="vertical" form={form} onFinish={onFinish}>
              <Tabs
              defaultActiveKey="1"
              activeKey={activeKey}
              items={tabsItems} 
              onChange={(e) => {
                if (e == "3" || e == "1") {
                  setActiveKey(e)
                }
              }}
            />
            </Form>
        </Drawer>
    )
})